import { useState, useCallback } from 'react';
import { MediaFile, MediaUploaderConfig } from '@/components/media/UniversalMediaUploader';
import { useToast } from '@/components/ui/use-toast';

interface UseMediaUploaderOptions {
  config?: Partial<MediaUploaderConfig>;
  onUpload?: (files: MediaFile[]) => Promise<void>;
  onSuccess?: (files: MediaFile[]) => void;
  onError?: (error: Error) => void;
}

export function useMediaUploader(options: UseMediaUploaderOptions = {}) {
  const [isOpen, setIsOpen] = useState(false);
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();

  const config: MediaUploaderConfig = {
    maxFiles: 10,
    maxFileSize: 50 * 1024 * 1024, // 50MB
    acceptedTypes: ['image/*', 'video/*', 'audio/*'],
    showPreview: true,
    showFilters: true,
    showMetadata: true,
    allowMultiple: true,
    showProgress: true,
    autoUpload: false,
    mode: 'full',
    aspectRatio: 'free',
    quality: 'high',
    title: 'Media Uploader',
    description: 'Upload and manage your media files',
    ...options.config
  };

  const openUploader = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeUploader = useCallback(() => {
    setIsOpen(false);
    setFiles([]);
  }, []);

  const handleUpload = useCallback(async (uploadedFiles: MediaFile[]) => {
    setUploading(true);
    
    try {
      if (options.onUpload) {
        await options.onUpload(uploadedFiles);
      } else {
        // Default upload behavior - simulate upload
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      options.onSuccess?.(uploadedFiles);
      
      toast({
        title: "Upload Successful",
        description: `Successfully uploaded ${uploadedFiles.length} file(s)`,
      });
      
    } catch (error) {
      const err = error as Error;
      options.onError?.(err);
      
      toast({
        title: "Upload Failed",
        description: err.message || "There was an error uploading your files",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  }, [options, toast]);

  const handleFilesChange = useCallback((changedFiles: MediaFile[]) => {
    setFiles(changedFiles);
  }, []);

  return {
    isOpen,
    files,
    uploading,
    config,
    openUploader,
    closeUploader,
    handleUpload,
    handleFilesChange
  };
}

// Preset configurations for common use cases
export const MediaUploaderPresets = {
  // Profile/Avatar upload
  avatar: {
    maxFiles: 1,
    maxFileSize: 10 * 1024 * 1024, // 10MB
    acceptedTypes: ['image/*'],
    allowMultiple: false,
    aspectRatio: '1:1' as const,
    title: 'Upload Profile Picture',
    description: 'Choose a photo to represent your profile',
    mode: 'compact' as const
  },

  // Cover photo upload
  cover: {
    maxFiles: 1,
    maxFileSize: 20 * 1024 * 1024, // 20MB
    acceptedTypes: ['image/*', 'video/*'],
    allowMultiple: false,
    aspectRatio: '16:9' as const,
    title: 'Upload Cover Media',
    description: 'Upload an image or video for your cover',
    mode: 'full' as const
  },

  // Post media upload
  post: {
    maxFiles: 10,
    maxFileSize: 50 * 1024 * 1024, // 50MB
    acceptedTypes: ['image/*', 'video/*'],
    allowMultiple: true,
    showMetadata: true,
    title: 'Add Media to Post',
    description: 'Upload photos and videos to share',
    mode: 'full' as const
  },

  // Product images
  product: {
    maxFiles: 8,
    maxFileSize: 20 * 1024 * 1024, // 20MB
    acceptedTypes: ['image/*'],
    allowMultiple: true,
    showFilters: true,
    showMetadata: true,
    title: 'Product Images',
    description: 'Upload high-quality images of your product',
    mode: 'full' as const
  },

  // Document upload
  document: {
    maxFiles: 5,
    maxFileSize: 100 * 1024 * 1024, // 100MB
    acceptedTypes: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/*'],
    allowMultiple: true,
    showFilters: false,
    showMetadata: true,
    title: 'Upload Documents',
    description: 'Upload documents and files',
    mode: 'compact' as const
  },

  // Gallery upload
  gallery: {
    maxFiles: 50,
    maxFileSize: 30 * 1024 * 1024, // 30MB
    acceptedTypes: ['image/*', 'video/*'],
    allowMultiple: true,
    showFilters: true,
    showMetadata: true,
    title: 'Upload to Gallery',
    description: 'Add photos and videos to your gallery',
    mode: 'full' as const
  },

  // Event media
  event: {
    maxFiles: 20,
    maxFileSize: 40 * 1024 * 1024, // 40MB
    acceptedTypes: ['image/*', 'video/*'],
    allowMultiple: true,
    showMetadata: true,
    title: 'Event Media',
    description: 'Upload media for your event',
    mode: 'full' as const
  },

  // Message attachment
  message: {
    maxFiles: 5,
    maxFileSize: 25 * 1024 * 1024, // 25MB
    acceptedTypes: ['image/*', 'video/*', 'audio/*', 'application/pdf'],
    allowMultiple: true,
    showFilters: false,
    showMetadata: false,
    title: 'Attach Files',
    description: 'Attach files to your message',
    mode: 'compact' as const,
    autoUpload: true
  },

  // Recipe images
  recipe: {
    maxFiles: 6,
    maxFileSize: 15 * 1024 * 1024, // 15MB
    acceptedTypes: ['image/*'],
    allowMultiple: true,
    showFilters: true,
    title: 'Recipe Photos',
    description: 'Add delicious photos of your recipe',
    mode: 'full' as const
  },

  // Compact inline uploader
  inline: {
    maxFiles: 3,
    maxFileSize: 10 * 1024 * 1024, // 10MB
    acceptedTypes: ['image/*'],
    allowMultiple: true,
    showFilters: false,
    showMetadata: false,
    mode: 'inline' as const,
    autoUpload: true
  },

  // Campaign media
  campaign: {
    maxFiles: 5,
    maxFileSize: 30 * 1024 * 1024, // 30MB
    acceptedTypes: ['image/*', 'video/*'],
    allowMultiple: true,
    showFilters: true,
    showMetadata: true,
    title: 'Campaign Media',
    description: 'Upload compelling images and videos for your campaign',
    mode: 'full' as const
  },

  // Hotel/Accommodation images
  hotel: {
    maxFiles: 15,
    maxFileSize: 25 * 1024 * 1024, // 25MB
    acceptedTypes: ['image/*'],
    allowMultiple: true,
    showFilters: true,
    showMetadata: true,
    title: 'Property Images',
    description: 'Upload high-quality photos of your property',
    mode: 'full' as const
  },

  // Music/Audio content
  music: {
    maxFiles: 10,
    maxFileSize: 100 * 1024 * 1024, // 100MB
    acceptedTypes: ['audio/*', 'image/*'],
    allowMultiple: true,
    showFilters: true,
    showMetadata: true,
    title: 'Music Upload',
    description: 'Upload audio tracks and cover art',
    mode: 'full' as const
  },

  // Group media
  group: {
    maxFiles: 8,
    maxFileSize: 20 * 1024 * 1024, // 20MB
    acceptedTypes: ['image/*', 'video/*'],
    allowMultiple: true,
    showFilters: true,
    showMetadata: true,
    title: 'Group Media',
    description: 'Upload images and videos for your group',
    mode: 'full' as const
  },

  // KYC documents
  kyc: {
    maxFiles: 10,
    maxFileSize: 50 * 1024 * 1024, // 50MB
    acceptedTypes: ['image/*', 'application/pdf'],
    allowMultiple: true,
    showFilters: false,
    showMetadata: true,
    title: 'KYC Documents',
    description: 'Upload identity verification documents',
    mode: 'full' as const
  }
};

// Helper function to get preset configuration
export function getMediaUploaderPreset(presetName: keyof typeof MediaUploaderPresets): MediaUploaderConfig {
  return MediaUploaderPresets[presetName];
}
